import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { DealerFullModel } from '../../../../shared/dealer';
import { MaterialIcon, UserStateComponent } from '../../../components';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { Fetcher, useFetcher } from '../../../utils';

export const VendorAssistantComponent: FC<{
  readonly dealer: DealerFullModel | undefined;
  readonly dealerAssistantLimit: number;
}> = ({ dealer, dealerAssistantLimit }) => {
  const { ts } = useTranslation();
  return (
    <UserStateComponent>
      <InnerVendorAssistant dealer={dealer} dealerAssistantLimit={dealerAssistantLimit} />
      <Row className="justify-content-center">
        <Col lg={6} xs={12}>
          <Link to="/vendor">
            <Button block color="primary" style={{ marginTop: '20px' }} type="submit">
              {ts('common.go_back')}
            </Button>
          </Link>
        </Col>
      </Row>
    </UserStateComponent>
  );
};

const InnerVendorAssistant: FC<{
  readonly dealer: DealerFullModel | undefined;
  readonly dealerAssistantLimit: number;
}> = ({ dealer, dealerAssistantLimit }) => {
  const { ts } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const noValidDealer = !dealer || !dealer.applications.some((t) => t.status === 'approved');

  const fetcher = useFetcher(async () => {
    if (noValidDealer) {
      return [];
    }

    return await api.getDealerAssistants(dealer.id);
  });

  if (noValidDealer) {
    return (
      <Row className="justify-content-center" id="vendorUnapproved">
        <Col lg={6} xs={12}>
          <Card className="warning">
            <CardBody className="text-center">
              <MaterialIcon large name="assignment_late" type="warning" />
              <CardText tag="div">
                <p>{ts('as_your_vendor_application_has')}</p>
                <p>{ts('common.please_check_back_later')}</p>
              </CardText>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="justify-content-center" id="dealerForm">
      <Col lg={3} xs={12}>
        <Card>
          <CardBody>
            <CardTitle>{ts('add_assistant')}</CardTitle>
            <p>{ts('you_can_nominate_up_to', dealerAssistantLimit)}</p>
            <p>{ts('when_you_add_a_new')}</p>
            <hr />
            <JSONForm
              method="post"
              onSuccess={() => {
                fetcher.refresh();
                toast.success(ts('weve_sent_your_assistant_request'));
              }}
              path={`/api/dealers/${dealer.id}/assistants`}
            >
              <FormGroup>
                <Label for="username">{ts('assistant_username')}</Label>
                <Input id="username" name="username" />
                <FormText color="muted">{ts('your_assistant_can_find_their')}</FormText>
              </FormGroup>
              <FormGroup>
                <Button block color="primary" id="submitForm" type="submit">
                  {ts('add_assistant')}
                </Button>
              </FormGroup>
            </JSONForm>
          </CardBody>
        </Card>
      </Col>
      <Col lg={7} xs={12}>
        <Card className="mb-10">
          <CardBody>
            <CardTitle>{ts('current_assistants')}</CardTitle>
            {!fetcher.success && <Fetcher result={fetcher} />}
            {fetcher.data && fetcher.data.length === 0 && (
              <p className="text-muted">{ts('you_dont_have_any_assistants')}</p>
            )}
            {fetcher.data && fetcher.data.length > 0 && (
              <Col xs={12}>
                {fetcher.data.map((assistant) => (
                  <Col
                    className="clearfix-after"
                    id={`assistant${assistant.user.id}`}
                    key={assistant.user.id}
                    xs={12}
                  >
                    <div className="float-end">
                      <JSONForm
                        method="delete"
                        onSuccess={() => {
                          fetcher.refresh();
                          toast.success(ts('assistant_removed'));
                        }}
                        path={`/api/dealers/${dealer.id}/assistants/${assistant.user.id}`}
                      >
                        <Button className="action-delete" color="danger" type="submit">
                          <MaterialIcon name="delete" />
                        </Button>
                      </JSONForm>
                    </div>
                    <h4>{assistant.user.username}</h4>
                    <div>
                      {ts(
                        assistant.accepted ? 'accepted' : 'pending_email_confirmation_please_ask',
                      )}
                    </div>
                    <hr />
                  </Col>
                ))}
              </Col>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
